import { DatePipe } from '@angular/common';
import { Component, effect, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '@core/auth/auth.service';
import { RettighedsType } from '@core/auth/rettighed.model';
import { CprNummerPipe } from '@core/pipes/cprnummer.pipe';
import diff, { Difference } from 'microdiff';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { LetregisterService } from '../letregister.service';
import { LetsikringRevision } from '../letsikring-revision.model';
import { Letsikring } from '../letsikring.model';
import { LetregisterDiffComponent } from './letregister-diff/letregister-diff.component';
import { LetsikringRevisionComponent } from './letsikring-revision/letsikring-revision.component';
import { LetLoadingIndicatorComponent } from '@shared/let-loading-indicator/let-loading-indicator.component';

@Component({
  selector: 'app-letregister-detail',
  imports: [
    CprNummerPipe,
    DatePipe,
    Select,
    ScrollPanelModule,
    DialogModule,
    FormsModule,
    ButtonModule,
    Textarea,
    InputGroupModule,
    InputGroupAddonModule,
    LetregisterDiffComponent,
    LetsikringRevisionComponent,
    LetLoadingIndicatorComponent,
  ],
  templateUrl: './letregister-detail.component.html',
  styleUrl: './letregister-detail.component.css',
})
export class LetregisterDetailComponent {
  authService = inject(AuthService);
  letregisterService = inject(LetregisterService);
  letsikringRevisionComponent = viewChild<LetsikringRevisionComponent>(
    'letsikringRevisionComponent'
  );

  selectedRevision = signal<LetsikringRevision | undefined>(undefined);
  editMode = signal(false);
  showDiff = signal(false);
  showSave = signal(false);
  kommentar = signal('');
  comparison = signal<Difference[]>([]);

  originalValues = {};

  allowEdit() {
    return this.authService.hasRettighed(RettighedsType.AdminLetregisterSkriv);
  }

  constructor() {
    effect(() => {
      const revNr =
        this.letregisterService.selectedLetsikring()?.gaeldendeRevision
          ?.revisionNr;
      const rev = this.letregisterService
        .selectedLetsikring()
        ?.revisioner?.find(x => x.revisionNr == revNr);
      if (rev) {
        this.selectRevision(rev);
      }
      this.editMode.set(false);
    });
  }

  redigerAftale() {
    this.editMode.set(true);
  }

  annuller() {
    this.fortryd();
    this.editMode.set(false);
  }

  fortryd() {
    const letsikring = this.letregisterService.selectedLetsikring();
    this.resetForm(letsikring);
  }

  resetForm(letsikring?: Letsikring) {
    this.letsikringRevisionComponent()!.resetForm(letsikring);
    this.originalValues = this.letsikringRevisionComponent()!.form.value;
  }

  visAendringer() {
    const letsikring = this.letregisterService.selectedLetsikring();
    const newValues = this.selectedRevision();
    if (newValues) {
      const oldValues = letsikring?.revisioner.find(
        x => x.revisionNr == newValues.revisionNr - 1
      );
      if (oldValues) {
        const changedValues = diff(oldValues, newValues);
        this.comparison.set(changedValues);
        this.showDiff.set(true);
      }
    }
  }

  visGem() {
    const newValues = this.letsikringRevisionComponent()!.form.value;
    const changedValues = diff(this.originalValues, newValues);
    this.comparison.set(changedValues);
    this.showSave.set(true);
  }

  async gemAendringer() {
    this.letsikringRevisionComponent()!.form.patchValue({
      gaeldendeRevision: {
        kommentar: this.kommentar(),
      },
    });
    const values = this.letsikringRevisionComponent()!.form
      .value as Partial<Letsikring>;
    this.showSave.set(false);
    // debugger;
    await this.letregisterService.gemLetsikring(values);
    this.kommentar.set('');
  }

  firstRevision() {
    const allRevisions =
      this.letregisterService.selectedLetsikring()?.revisioner || [];
    this.selectRevision(allRevisions[allRevisions.length - 1]);
  }

  lastRevision() {
    const allRevisions =
      this.letregisterService.selectedLetsikring()?.revisioner || [];
    this.selectRevision(allRevisions[0]);
  }

  nextRevision() {
    const currentRevisionNr = this.selectedRevision()?.revisionNr || 0;
    this.selectRevisionNr(currentRevisionNr + 1);
  }

  previousRevision() {
    const currentRevisionNr = this.selectedRevision()?.revisionNr || 0;
    this.selectRevisionNr(currentRevisionNr - 1);
  }

  selectRevisionNr(revisionNr: number) {
    const allRevisions =
      this.letregisterService.selectedLetsikring()?.revisioner || [];
    let found: LetsikringRevision[] = [];
    found = allRevisions.filter(r => r.revisionNr === revisionNr);
    if (found.length > 0) {
      this.selectRevision(found[0]);
    }
  }

  selectRevision(rev: LetsikringRevision) {
    this.selectedRevision.set(rev);
  }
}
